import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Chart, registerables } from 'chart.js'
import { useApp } from '../context/AppContext'
import HeaderSection from '../components/Shared/HeaderSection'
import AIAnalysisPanel from '../components/Home/AIAnalysisPanel'
import MyDashboard from '../components/Home/MyDashboard'
import useHasAccess from '../hooks/useHasAccess'
import SegmentedControl from '../components/Shared/SegmentedControl'
import usePersistedState from '../hooks/usePersistedState'
import queueService from '../services/queueService'
import { summarizeByQueue } from '../utils/queue'
import {
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
} from '../services/incidentService'

Chart.register(...registerables)

// ─── Configuración y Utilidades ──────────────────────────────────────────────

const C = {
  blue: '#3b82f6',
  indigo: '#6366f1',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  slate: '#64748b',
  teal: '#14b8a6',
  pink: '#ec4899',
}

const PALETTE = [
  C.amber,
  C.blue,
  C.green,
  C.indigo,
  C.red,
  C.slate,
  C.teal,
  C.pink,
]

// Color semántico por prioridad (cae a slate si no coincide).
const PRIORITY_COLOR = {
  Basico: C.slate,
  Medio: C.blue,
  Alto: C.amber,
  Crítico: C.red,
}

// Color semántico por severidad de incidente SGSI.
const SEVERITY_COLOR = {
  Crítico: C.red,
  Alto: C.amber,
  Medio: C.blue,
  Bajo: C.slate,
}

// Color semántico por estado de incidente SGSI.
const INCIDENT_STATUS_COLOR = {
  Abierto: C.red,
  'En curso': C.amber,
  Cerrado: C.green,
}

/**
 * Permiso de soporte: da acceso al dashboard operativo y a los incidentes
 * (SGSI). Sin él se muestra el dashboard personal.
 */
const INCIDENTS_ACCESS = 'ticket_support'

/**
 * Rangos temporales disponibles en el filtro del dashboard.
 * `days` es null en «Todo» (sin recorte por fecha).
 */
const RANGES = [
  { key: '7d', label: '7 días', days: 7 },
  { key: '30d', label: '30 días', days: 30 },
  { key: '90d', label: '90 días', days: 90 },
  { key: '6m', label: '6 meses', days: 183 },
  { key: '12m', label: '12 meses', days: 365 },
  { key: 'all', label: 'Todo', days: null },
]

const DEFAULT_RANGE = '6m'

const groupBy = (arr, fn) =>
  arr.reduce((acc, x) => {
    const k = fn(x)
    ;(acc[k] = acc[k] || []).push(x)
    return acc
  }, {})

const startOfDay = (date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Fecha de inicio del rango seleccionado, o null si es «Todo».
 * @param {string} rangeKey
 * @returns {Date|null}
 */
const rangeStart = (rangeKey) => {
  const range = RANGES.find((r) => r.key === rangeKey)
  if (!range?.days) return null
  const start = startOfDay(new Date())
  start.setDate(start.getDate() - (range.days - 1))
  return start
}

/**
 * Filtra por fecha dentro del rango. Los registros sin fecha se descartan
 * salvo que el rango sea «Todo».
 * @param {Array} rows
 * @param {string} dateKey - Campo de fecha a evaluar.
 * @param {Date|null} start
 */
const withinRange = (rows, dateKey, start) => {
  if (!start) return rows
  const from = start.getTime()
  return rows.filter((r) => {
    const value = r[dateKey]
    if (!value) return false
    const time = new Date(value).getTime()
    return !Number.isNaN(time) && time >= from
  })
}

/**
 * Construye los intervalos del eje temporal según el rango: días para rangos
 * cortos, semanas para 90 días y meses para los largos.
 * @param {string} rangeKey
 * @param {Date|null} oldest - Fecha más antigua con datos (para «Todo»).
 * @returns {Array<{label: string, start: number, end: number}>}
 */
const buildBuckets = (rangeKey, oldest) => {
  const now = new Date()

  const makeDaily = (days) =>
    Array.from({ length: days }, (_, i) => {
      const start = startOfDay(now)
      start.setDate(start.getDate() - (days - 1 - i))
      const end = new Date(start)
      end.setDate(end.getDate() + 1)
      return {
        label: start.toLocaleDateString('es', {
          day: '2-digit',
          month: 'short',
        }),
        start: start.getTime(),
        end: end.getTime(),
      }
    })

  if (rangeKey === '7d') return makeDaily(7)
  if (rangeKey === '30d') return makeDaily(30)

  if (rangeKey === '90d') {
    return Array.from({ length: 13 }, (_, i) => {
      const start = startOfDay(now)
      start.setDate(start.getDate() - 7 * (12 - i))
      const end = new Date(start)
      end.setDate(end.getDate() + 7)
      return {
        label: start.toLocaleDateString('es', {
          day: '2-digit',
          month: 'short',
        }),
        start: start.getTime(),
        end: end.getTime(),
      }
    })
  }

  let months = rangeKey === '12m' ? 12 : 6
  if (rangeKey === 'all') {
    const from = oldest || now
    const diff =
      (now.getFullYear() - from.getFullYear()) * 12 +
      (now.getMonth() - from.getMonth()) +
      1
    // Techo de 24 meses para que el eje siga siendo legible.
    months = Math.min(Math.max(diff, 1), 24)
  }

  return Array.from({ length: months }, (_, i) => {
    const start = new Date(
      now.getFullYear(),
      now.getMonth() - (months - 1 - i),
      1
    )
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1)
    const month = start.toLocaleString('es', { month: 'short' })
    return {
      // Con más de 12 meses repetimos nombres de mes: añadimos el año.
      label:
        months > 12
          ? `${month} ${String(start.getFullYear()).slice(2)}`
          : month,
      start: start.getTime(),
      end: end.getTime(),
    }
  })
}

/**
 * Cuenta cuántos registros caen en cada intervalo según un campo de fecha.
 * @param {Array} buckets
 * @param {Array} rows
 * @param {(row: Object) => string|null} pickDate - Extrae la fecha del registro.
 */
const countByBucket = (buckets, rows, pickDate) => {
  const counts = new Array(buckets.length).fill(0)
  for (const row of rows) {
    const raw = pickDate(row)
    if (!raw) continue
    const time = new Date(raw).getTime()
    if (Number.isNaN(time)) continue
    const idx = buckets.findIndex((b) => time >= b.start && time < b.end)
    if (idx >= 0) counts[idx]++
  }
  return counts
}

// Formatea una duración en milisegundos a algo legible: "3.4 h" o "2d 5h".
const fmtDuration = (ms) => {
  if (!ms || ms <= 0 || Number.isNaN(ms)) return '—'
  const hours = ms / 3600000
  if (hours < 1) return `${Math.round(ms / 60000)} min`
  if (hours < 24) return `${hours.toFixed(1)} h`
  const days = Math.floor(hours / 24)
  const rem = Math.round(hours % 24)
  return rem ? `${days}d ${rem}h` : `${days}d`
}

// Promedio de (fin - inicio) en ms sobre los registros que tengan ambas fechas.
const avgSpan = (rows, endKey, startKey = 'createdAt') => {
  const spans = rows
    .filter((t) => t[endKey] && t[startKey])
    .map((t) => new Date(t[endKey]) - new Date(t[startKey]))
    .filter((ms) => ms >= 0)
  if (!spans.length) return null
  return spans.reduce((a, b) => a + b, 0) / spans.length
}

// ─── Sub-Componentes Visuales ────────────────────────────────────────────────

const LoadingState = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '50vh',
      gap: 14,
    }}
  >
    <div
      className="spinner-border"
      style={{ width: '2rem', height: '2rem', color: C.blue }}
      role="status"
    >
      <span className="visually-hidden">Cargando...</span>
    </div>
    <p
      style={{
        color: 'var(--text-muted)',
        fontSize: 13,
        margin: 0,
      }}
    >
      Preparando datos…
    </p>
  </div>
)

/** Selector de rango temporal (segmented control). */
const RangeFilter = ({ value, onChange }) => (
  <div
    role="group"
    aria-label="Rango de fechas"
    style={{
      display: 'inline-flex',
      gap: 2,
      padding: 3,
      borderRadius: 10,
      background: 'var(--bg-surface-2)',
      border: '1px solid var(--border)',
      flexWrap: 'wrap',
    }}
  >
    {RANGES.map((r) => {
      const active = r.key === value
      return (
        <button
          key={r.key}
          type="button"
          onClick={() => onChange(r.key)}
          aria-pressed={active}
          style={{
            border: 'none',
            borderRadius: 8,
            padding: '5px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all .15s ease',
            background: active ? 'var(--bg-surface)' : 'transparent',
            color: active ? 'var(--text)' : 'var(--text-2)',
            boxShadow:
              active ? '0 1px 2px var(--shadow)' : 'none',
          }}
        >
          {r.label}
        </button>
      )
    })}
  </div>
)

const KPI = ({ label, value, accent, hint }) => (
  <div
    style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }}
  >
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        color: 'var(--text-2)',
        fontSize: 12,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: accent,
          flexShrink: 0,
        }}
      />
      {label}
    </span>
    <span
      style={{
        color: 'var(--text)',
        fontSize: 24,
        fontWeight: 600,
        lineHeight: 1.1,
      }}
    >
      {value ?? '0'}
    </span>
    {hint && (
      <span
        style={{
          color: 'var(--text-muted)',
          fontSize: 11,
        }}
      >
        {hint}
      </span>
    )}
  </div>
)

const SectionTitle = ({ children }) => (
  <h3
    style={{
      color: 'var(--text-2)',
      fontSize: 13,
      fontWeight: 600,
      marginBottom: 14,
    }}
  >
    {children}
  </h3>
)

const ChartCard = ({ title, children, flex = 1 }) => (
  <div
    style={{
      flex,
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: 16,
      minWidth: 0,
    }}
  >
    <SectionTitle>{title}</SectionTitle>
    {children}
  </div>
)

// Encabezado de grupo de secciones (Tendencia, Distribución, etc.)
const GroupLabel = ({ children, accent = C.indigo }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      margin: '4px 0 10px',
    }}
  >
    <span
      style={{ width: 3, height: 14, borderRadius: 10, background: accent }}
    />
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </span>
  </div>
)

/** Mensaje para secciones sin datos en el rango elegido. */
const EmptyHint = ({ children }) => (
  <p
    style={{
      color: 'var(--text-muted)',
      fontSize: 13,
      margin: 0,
      padding: '18px 0',
      textAlign: 'center',
    }}
  >
    {children}
  </p>
)

// ─── Componente Principal ───────────────────────────────────────────────────

/**
 * Dashboard operativo (soporte): tickets de toda la empresa, colas, personas,
 * incidentes y análisis con IA. Sólo se monta con permiso de soporte.
 */
const OperationalDashboard = () => {
  const { supabase, access = [] } = useApp()
  const [isDark, setIsDark] = useState(false)
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState(DEFAULT_RANGE)

  const lineRef = useRef(null)
  const doughnutRef = useRef(null)
  const barRef = useRef(null)
  const priorityRef = useRef(null)
  const categoryRef = useRef(null)
  const responsableRef = useRef(null)
  const creatorsRef = useRef(null)
  const incTrendRef = useRef(null)
  const incSeverityRef = useRef(null)
  const incStatusRef = useRef(null)
  const incSourceRef = useRef(null)
  const queueRef = useRef(null)

  const [allTickets, setAllTickets] = useState([])
  const [statuses, setStatuses] = useState([])
  const [priorities, setPriorities] = useState([])
  const [categories, setCategories] = useState([])
  const [employees, setEmployees] = useState([])
  const [deletedCount, setDeletedCount] = useState(0)
  const [allIncidents, setAllIncidents] = useState([])
  const [queues, setQueues] = useState([])
  const [tab, setTab] = usePersistedState('dashboard.tab', 'tickets')

  // Sólo consultamos el SGSI si el usuario tiene el permiso del módulo.
  const canSeeIncidents = useMemo(
    () =>
      access?.some(
        (p) => p?.vista_slug === INCIDENTS_ACCESS && p?.puede_ver === true
      ) ?? false,
    [access]
  )

  // 1. Monitorizar cambio de tema
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const theme = document.documentElement.getAttribute('data-bs-theme')
      setIsDark(theme === 'dark')
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    setIsDark(document.documentElement.getAttribute('data-bs-theme') === 'dark')
    return () => observer.disconnect()
  }, [])

  // 2. Carga de datos (una sola vez: el rango se aplica en cliente)
  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [t, s, p, c, e, d, inc, q] = await Promise.all([
        supabase
          .schema('Tickets')
          .from('ticket')
          .select(
            'id,createdAt,systemOrigin,statusId,completed,userId,priorityId,categoryId,responsibleId,queueId,type,impact,urgency,dueAt,firstResponseAt,resolvedAt,totalTime'
          )
          .eq('deleted', false),
        supabase.schema('Tickets').from('status').select('id,name'),
        supabase.schema('Tickets').from('priority').select('id,name'),
        supabase.schema('Tickets').from('category').select('id,name'),
        supabase
          .schema('public')
          .from('Empleados')
          .select('id,id_user,nombre,apellido'),
        // Conteo de tickets con borrado lógico (solo el total, sin filas).
        supabase
          .schema('Tickets')
          .from('ticket')
          .select('id', { count: 'exact', head: true })
          .eq('deleted', true),
        // Incidentes de seguridad (SGSI). Sin permiso ni siquiera se consulta.
        canSeeIncidents
          ? supabase
              .schema('InventoryIT')
              .from('SgsiIncident')
              .select(
                'Id,Code,DetectedAt,Severity,Status,ResponseDueAt,ClosedAt,DetectionSource,PersonalDataAffected,ProdhabRequired,ProdhabNotifiedAt,CreatedAt'
              )
          : Promise.resolve({ data: [] }),
        queueService.getAll(),
      ])
      setAllTickets(t.data || [])
      setStatuses(s.data || [])
      setPriorities(p.data || [])
      setCategories(c.data || [])
      setEmployees(e.data || [])
      setDeletedCount(d.count || 0)
      setAllIncidents(inc.data || [])
      setQueues(q.success ? q.data : [])
    } catch (err) {
      console.error('Error fetching data:', err)
    } finally {
      setTimeout(() => setLoading(false), 600)
    }
  }, [supabase, canSeeIncidents])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // ─── Recorte por rango ─────────────────────────────────────────────────────

  const from = useMemo(() => rangeStart(range), [range])
  const rangeLabel = useMemo(
    () => RANGES.find((r) => r.key === range)?.label ?? '',
    [range]
  )

  const tickets = useMemo(
    () => withinRange(allTickets, 'createdAt', from),
    [allTickets, from]
  )
  const incidents = useMemo(
    () => withinRange(allIncidents, 'DetectedAt', from),
    [allIncidents, from]
  )

  // Fecha más antigua con datos: define el ancho del eje en «Todo».
  const oldest = useMemo(() => {
    const times = [
      ...allTickets.map((t) => new Date(t.createdAt).getTime()),
      ...allIncidents.map((i) => new Date(i.DetectedAt).getTime()),
    ].filter((n) => !Number.isNaN(n))
    return times.length ? new Date(Math.min(...times)) : null
  }, [allTickets, allIncidents])

  const buckets = useMemo(() => buildBuckets(range, oldest), [range, oldest])
  const lineLabels = useMemo(() => buckets.map((b) => b.label), [buckets])

  // ─── Datos derivados (memoizados) ──────────────────────────────────────────

  const statusMap = useMemo(
    () => Object.fromEntries(statuses.map((s) => [s.id, s.name])),
    [statuses]
  )
  const categoryMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.name])),
    [categories]
  )
  const employeeMap = useMemo(
    () =>
      Object.fromEntries(
        employees.map((e) => [
          e.id,
          `${e.nombre || ''} ${e.apellido || ''}`.trim() || '—',
        ])
      ),
    [employees]
  )
  // Mapa por auth id (id_user). El campo ticket.userId guarda el auth id del
  // creador, no el id de Empleados, así que necesitamos esta segunda tabla.
  const employeeByAuth = useMemo(
    () =>
      Object.fromEntries(
        employees
          .filter((e) => e.id_user)
          .map((e) => [
            e.id_user,
            `${e.nombre || ''} ${e.apellido || ''}`.trim() || '—',
          ])
      ),
    [employees]
  )

  const lineData = useMemo(
    () => countByBucket(buckets, tickets, (t) => t.createdAt),
    [buckets, tickets]
  )

  // Completados por fecha de resolución; si falta, cae a la de creación.
  const completedData = useMemo(
    () =>
      countByBucket(
        buckets,
        tickets.filter((t) => t.completed),
        (t) => t.resolvedAt || t.createdAt
      ),
    [buckets, tickets]
  )

  const byStatus = useMemo(
    () => groupBy(tickets, (t) => statusMap[t.statusId] || 'Sin estado'),
    [tickets, statusMap]
  )
  const statusLabels = useMemo(() => Object.keys(byStatus), [byStatus])
  const statusData = useMemo(
    () => Object.values(byStatus).map((a) => a.length),
    [byStatus]
  )

  const byOrigin = useMemo(
    () => groupBy(tickets, (t) => t.systemOrigin || 'Desconocido'),
    [tickets]
  )
  const originLabels = useMemo(() => Object.keys(byOrigin), [byOrigin])
  const originData = useMemo(
    () => Object.values(byOrigin).map((a) => a.length),
    [byOrigin]
  )

  // Por prioridad (en orden del catálogo para colores coherentes)
  const priorityLabels = useMemo(
    () => priorities.map((p) => p.name),
    [priorities]
  )
  const priorityData = useMemo(
    () =>
      priorities.map(
        (p) => tickets.filter((t) => t.priorityId === p.id).length
      ),
    [priorities, tickets]
  )

  // Por categoría
  const byCategory = useMemo(
    () => groupBy(tickets, (t) => categoryMap[t.categoryId] || 'Sin categoría'),
    [tickets, categoryMap]
  )
  const categoryLabels = useMemo(() => Object.keys(byCategory), [byCategory])
  const categoryData = useMemo(
    () => Object.values(byCategory).map((a) => a.length),
    [byCategory]
  )

  // Carga por responsable (top 8, "Sin asignar" para los nulos)
  const responsable = useMemo(() => {
    const grouped = groupBy(tickets, (t) =>
      t.responsibleId
        ? employeeMap[t.responsibleId] || 'Desconocido'
        : 'Sin asignar'
    )
    return Object.entries(grouped)
      .map(([name, arr]) => ({
        name,
        total: arr.length,
        abiertos: arr.filter((t) => !t.completed).length,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
  }, [tickets, employeeMap])

  // Top creadores: qué usuario abre más tickets (ticket.userId = auth id)
  const creators = useMemo(() => {
    const grouped = groupBy(tickets, (t) =>
      t.userId ? employeeByAuth[t.userId] || 'Desconocido' : 'Sin autor'
    )
    return Object.entries(grouped)
      .map(([name, arr]) => ({ name, total: arr.length }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
  }, [tickets, employeeByAuth])

  // Tiempos medios por prioridad (SLA)
  const slaByPriority = useMemo(
    () =>
      priorities.map((p) => {
        const arr = tickets.filter((t) => t.priorityId === p.id)
        return {
          name: p.name,
          total: arr.length,
          firstResponse: avgSpan(arr, 'firstResponseAt'),
          resolution: avgSpan(arr, 'resolvedAt'),
        }
      }),
    [priorities, tickets]
  )

  // ─── KPIs de tickets ──────────────────────────────────────────────────────
  const total = tickets.length
  const resueltos = tickets.filter((t) => t.completed).length
  const pendientes = total - resueltos
  const sinAsignar = tickets.filter((t) => !t.responsibleId).length
  const now = Date.now()
  const vencidos = tickets.filter(
    (t) => t.dueAt && !t.completed && new Date(t.dueAt).getTime() < now
  ).length
  const tasaResolucion = total ? Math.round((resueltos / total) * 100) : 0
  const avgFirstResponse = useMemo(
    () => avgSpan(tickets, 'firstResponseAt'),
    [tickets]
  )
  const avgResolution = useMemo(() => avgSpan(tickets, 'resolvedAt'), [tickets])

  // ─── Incidentes de seguridad (SGSI) ───────────────────────────────────────

  const incTotal = incidents.length
  const incAbiertos = useMemo(
    () => incidents.filter((i) => i.Status !== 'Cerrado').length,
    [incidents]
  )
  const incCriticos = useMemo(
    () =>
      incidents.filter(
        (i) => i.Severity === 'Crítico' && i.Status !== 'Cerrado'
      ).length,
    [incidents]
  )
  const incVencidos = useMemo(
    () =>
      incidents.filter(
        (i) =>
          i.Status !== 'Cerrado' &&
          i.ResponseDueAt &&
          new Date(i.ResponseDueAt).getTime() < now
      ).length,
    [incidents, now]
  )
  const incDatosPersonales = useMemo(
    () => incidents.filter((i) => i.PersonalDataAffected).length,
    [incidents]
  )
  const incProdhab = useMemo(
    () =>
      incidents.filter((i) => i.ProdhabRequired && !i.ProdhabNotifiedAt).length,
    [incidents]
  )
  const incAvgClose = useMemo(
    () => avgSpan(incidents, 'ClosedAt', 'DetectedAt'),
    [incidents]
  )

  const incSeverityData = useMemo(
    () =>
      INCIDENT_SEVERITIES.map(
        (s) => incidents.filter((i) => i.Severity === s).length
      ),
    [incidents]
  )
  const incStatusData = useMemo(
    () =>
      INCIDENT_STATUSES.map(
        (s) => incidents.filter((i) => i.Status === s).length
      ),
    [incidents]
  )
  const incSources = useMemo(() => {
    const grouped = groupBy(
      incidents,
      (i) => i.DetectionSource || 'Sin especificar'
    )
    return Object.entries(grouped)
      .map(([name, arr]) => ({ name, total: arr.length }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6)
  }, [incidents])

  const incDetectedData = useMemo(
    () => countByBucket(buckets, incidents, (i) => i.DetectedAt),
    [buckets, incidents]
  )
  const incClosedData = useMemo(
    () => countByBucket(buckets, incidents, (i) => i.ClosedAt),
    [buckets, incidents]
  )

  const showIncidents = canSeeIncidents && allIncidents.length > 0

  // Pestaña efectiva: sin permiso SGSI sólo existe la de tickets.
  const activeTab = canSeeIncidents ? tab : 'tickets'

  // ─── Colas de atención ──────────────────────────────────────────────────────
  const queueSummary = useMemo(
    () => summarizeByQueue(tickets, queues, now),
    [tickets, queues, now]
  )

  // Métricas agregadas que se envían a la IA (solo números y nombres de
  // catálogo — sin datos sensibles de tickets ni incidentes individuales).
  const aiMetrics = useMemo(
    () => ({
      rango: rangeLabel,
      totales: {
        total,
        pendientes,
        completados: resueltos,
        sinAsignar,
        vencidos,
        eliminados: deletedCount,
        tasaResolucionPct: tasaResolucion,
      },
      tiemposPromedio: {
        primeraRespuesta: fmtDuration(avgFirstResponse),
        resolucion: fmtDuration(avgResolution),
      },
      porEstado: statusLabels.map((s, i) => ({
        estado: s,
        total: statusData[i],
      })),
      porPrioridad: priorityLabels.map((p, i) => ({
        prioridad: p,
        total: priorityData[i],
      })),
      porCategoria: categoryLabels.map((c, i) => ({
        categoria: c,
        total: categoryData[i],
      })),
      porOrigen: originLabels.map((o, i) => ({
        origen: o,
        total: originData[i],
      })),
      cargaPorResponsable: responsable,
      topCreadores: creators,
      slaPorPrioridad: slaByPriority.map((r) => ({
        prioridad: r.name,
        tickets: r.total,
        primeraRespuesta: fmtDuration(r.firstResponse),
        resolucion: fmtDuration(r.resolution),
      })),
      tendencia: lineLabels.map((m, i) => ({
        periodo: m,
        creados: lineData[i],
        completados: completedData[i],
      })),
      ...(showIncidents && {
        incidentesSeguridad: {
          total: incTotal,
          abiertos: incAbiertos,
          criticosAbiertos: incCriticos,
          fueraDePlazo: incVencidos,
          conDatosPersonales: incDatosPersonales,
          pendientesNotificarProdhab: incProdhab,
          tiempoMedioCierre: fmtDuration(incAvgClose),
          porSeveridad: INCIDENT_SEVERITIES.map((s, i) => ({
            severidad: s,
            total: incSeverityData[i],
          })),
          porEstado: INCIDENT_STATUSES.map((s, i) => ({
            estado: s,
            total: incStatusData[i],
          })),
          porOrigenDeteccion: incSources,
          tendencia: lineLabels.map((m, i) => ({
            periodo: m,
            detectados: incDetectedData[i],
            cerrados: incClosedData[i],
          })),
        },
      }),
    }),
    [
      rangeLabel,
      total,
      pendientes,
      resueltos,
      sinAsignar,
      vencidos,
      deletedCount,
      tasaResolucion,
      avgFirstResponse,
      avgResolution,
      statusLabels,
      statusData,
      priorityLabels,
      priorityData,
      categoryLabels,
      categoryData,
      originLabels,
      originData,
      responsable,
      creators,
      slaByPriority,
      lineLabels,
      lineData,
      completedData,
      showIncidents,
      incTotal,
      incAbiertos,
      incCriticos,
      incVencidos,
      incDatosPersonales,
      incProdhab,
      incAvgClose,
      incSeverityData,
      incStatusData,
      incSources,
      incDetectedData,
      incClosedData,
    ]
  )

  // ─── Gráficos ──────────────────────────────────────────────────────────────
  const axisColors = (dark) => ({
    fontColor: dark ? 'rgba(255,255,255,0.5)' : '#64748b',
    gridColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  })

  // Leyenda compartida por todos los gráficos que la muestran.
  const legendLabels = (fontColor) => ({
    color: fontColor,
    font: { size: 11 },
    boxWidth: 12,
    boxHeight: 12,
    usePointStyle: true,
  })

  // Línea combinada: Creados vs Completados
  useEffect(() => {
    if (loading || !lineRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(lineRef.current, {
      type: 'line',
      data: {
        labels: lineLabels,
        datasets: [
          {
            label: 'Creados',
            data: lineData,
            borderColor: C.blue,
            backgroundColor: 'rgba(59,130,246,0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: lineLabels.length > 14 ? 0 : 3,
          },
          {
            label: 'Completados',
            data: completedData,
            borderColor: C.green,
            backgroundColor: 'rgba(34,197,94,0.08)',
            borderDash: [5, 5],
            fill: true,
            tension: 0.4,
            pointRadius: lineLabels.length > 14 ? 0 : 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { labels: legendLabels(fontColor) } },
        scales: {
          x: {
            ticks: {
              color: fontColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
            },
            grid: { color: gridColor },
          },
          y: {
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, lineLabels, lineData, completedData])

  // Barras apiladas: Tickets por cola (abiertos vs completados)
  useEffect(() => {
    if (loading || !queueRef.current || queueSummary.length === 0) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(queueRef.current, {
      type: 'bar',
      data: {
        labels: queueSummary.map((q) => q.name),
        datasets: [
          {
            label: 'Abiertos',
            data: queueSummary.map((q) => q.open),
            backgroundColor: queueSummary.map((q) => q.color),
            borderRadius: 4,
          },
          {
            label: 'Completados',
            data: queueSummary.map((q) => q.completed),
            backgroundColor: queueSummary.map((q) => `${q.color}55`),
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { labels: legendLabels(fontColor) } },
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
          },
          y: {
            stacked: true,
            ticks: { color: fontColor },
            grid: { display: false },
          },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, queueSummary])

  // Doughnut: Estados
  useEffect(() => {
    if (loading || !doughnutRef.current) return
    const { fontColor } = axisColors(isDark)
    const chart = new Chart(doughnutRef.current, {
      type: 'doughnut',
      data: {
        labels: statusLabels,
        datasets: [
          {
            data: statusData,
            backgroundColor: PALETTE,
            borderColor: isDark ? '#131c2e' : '#ffffff',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: { legend: { labels: legendLabels(fontColor) } },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, statusLabels, statusData])

  // Bar: Por Origen
  useEffect(() => {
    if (loading || !barRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(barRef.current, {
      type: 'bar',
      data: {
        labels: originLabels,
        datasets: [
          { data: originData, backgroundColor: C.indigo, borderRadius: 6 },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: fontColor }, grid: { color: gridColor } },
          y: {
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, originLabels, originData])

  // Bar: Por Prioridad (con colores semánticos)
  useEffect(() => {
    if (loading || !priorityRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(priorityRef.current, {
      type: 'bar',
      data: {
        labels: priorityLabels,
        datasets: [
          {
            data: priorityData,
            backgroundColor: priorityLabels.map(
              (l) => PRIORITY_COLOR[l] || C.slate
            ),
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: fontColor }, grid: { color: gridColor } },
          y: {
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, priorityLabels, priorityData])

  // Doughnut: Por Categoría
  useEffect(() => {
    if (loading || !categoryRef.current) return
    const { fontColor } = axisColors(isDark)
    const chart = new Chart(categoryRef.current, {
      type: 'doughnut',
      data: {
        labels: categoryLabels,
        datasets: [
          {
            data: categoryData,
            backgroundColor: PALETTE,
            borderColor: isDark ? '#131c2e' : '#ffffff',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: { legend: { labels: legendLabels(fontColor) } },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, categoryLabels, categoryData])

  // Bar horizontal: Carga por Responsable (apilado abiertos vs completados)
  useEffect(() => {
    if (loading || !responsableRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const labels = responsable.map((r) => r.name)
    const chart = new Chart(responsableRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Abiertos',
            data: responsable.map((r) => r.abiertos),
            backgroundColor: C.amber,
            borderRadius: 4,
            stack: 'carga',
          },
          {
            label: 'Completados',
            data: responsable.map((r) => r.total - r.abiertos),
            backgroundColor: C.green,
            borderRadius: 4,
            stack: 'carga',
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { labels: legendLabels(fontColor) } },
        scales: {
          x: {
            stacked: true,
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
          y: {
            stacked: true,
            ticks: { color: fontColor },
            grid: { display: false },
          },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, responsable])

  // Bar horizontal: Top creadores de tickets (quién abre más)
  useEffect(() => {
    if (loading || !creatorsRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(creatorsRef.current, {
      type: 'bar',
      data: {
        labels: creators.map((c) => c.name),
        datasets: [
          {
            label: 'Tickets creados',
            data: creators.map((c) => c.total),
            backgroundColor: C.blue,
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
          y: { ticks: { color: fontColor }, grid: { display: false } },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, creators])

  // Línea: Incidentes detectados vs cerrados
  useEffect(() => {
    if (loading || !showIncidents || !incTrendRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(incTrendRef.current, {
      type: 'line',
      data: {
        labels: lineLabels,
        datasets: [
          {
            label: 'Detectados',
            data: incDetectedData,
            borderColor: C.red,
            backgroundColor: 'rgba(239,68,68,0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: lineLabels.length > 14 ? 0 : 3,
          },
          {
            label: 'Cerrados',
            data: incClosedData,
            borderColor: C.green,
            backgroundColor: 'rgba(34,197,94,0.08)',
            borderDash: [5, 5],
            fill: true,
            tension: 0.4,
            pointRadius: lineLabels.length > 14 ? 0 : 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { labels: legendLabels(fontColor) } },
        scales: {
          x: {
            ticks: {
              color: fontColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
            },
            grid: { color: gridColor },
          },
          y: {
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
        },
      },
    })
    return () => chart.destroy()
  }, [
    loading,
    isDark,
    showIncidents,
    lineLabels,
    incDetectedData,
    incClosedData,
  ])

  // Bar: Incidentes por severidad
  useEffect(() => {
    if (loading || !showIncidents || !incSeverityRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(incSeverityRef.current, {
      type: 'bar',
      data: {
        labels: INCIDENT_SEVERITIES,
        datasets: [
          {
            data: incSeverityData,
            backgroundColor: INCIDENT_SEVERITIES.map(
              (s) => SEVERITY_COLOR[s] || C.slate
            ),
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: fontColor }, grid: { color: gridColor } },
          y: {
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, showIncidents, incSeverityData])

  // Doughnut: Incidentes por estado
  useEffect(() => {
    if (loading || !showIncidents || !incStatusRef.current) return
    const { fontColor } = axisColors(isDark)
    const chart = new Chart(incStatusRef.current, {
      type: 'doughnut',
      data: {
        labels: INCIDENT_STATUSES,
        datasets: [
          {
            data: incStatusData,
            backgroundColor: INCIDENT_STATUSES.map(
              (s) => INCIDENT_STATUS_COLOR[s] || C.slate
            ),
            borderColor: isDark ? '#131c2e' : '#ffffff',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        plugins: { legend: { labels: legendLabels(fontColor) } },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, showIncidents, incStatusData])

  // Bar horizontal: Origen de detección
  useEffect(() => {
    if (loading || !showIncidents || !incSourceRef.current) return
    const { fontColor, gridColor } = axisColors(isDark)
    const chart = new Chart(incSourceRef.current, {
      type: 'bar',
      data: {
        labels: incSources.map((s) => s.name),
        datasets: [
          {
            label: 'Incidentes',
            data: incSources.map((s) => s.total),
            backgroundColor: C.teal,
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
          y: { ticks: { color: fontColor }, grid: { display: false } },
        },
      },
    })
    return () => chart.destroy()
  }, [loading, isDark, showIncidents, incSources])

  return (
    <div
      style={{
        minHeight: '100%',
        color: 'var(--text)',
        padding: '10px 0',
        transition: 'all 0.3s ease',
      }}
    >
      <HeaderSection
        compact
        title="Dashboard Operativo"
        subtitle="Tickets, colas de atención e incidentes en tiempo real"
        icon="fa-solid fa-chart-pie"
      />

      {/* Filtro de rango temporal */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          {canSeeIncidents && (
            <SegmentedControl
              ariaLabel="Sección del dashboard"
              options={[
                {
                  value: 'tickets',
                  label: 'Tickets',
                  icon: 'fa-solid fa-ticket',
                },
                {
                  value: 'incidents',
                  label: 'Incidentes',
                  icon: 'fa-solid fa-shield-halved',
                },
              ]}
              value={activeTab}
              onChange={setTab}
            />
          )}
          <RangeFilter value={range} onChange={setRange} />
        </div>
        <span
          style={{
            fontSize: 12,
            color: 'var(--text-muted)',
          }}
        >
          {range === 'all'
            ? 'Mostrando el histórico completo'
            : `Mostrando los últimos ${rangeLabel.toLowerCase()}`}
        </span>
      </div>

      {loading ? (
        <LoadingState />
      ) : (
        <>
          <div style={{ display: activeTab === 'tickets' ? 'block' : 'none' }}>
            {/* KPIs */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 12,
                marginBottom: 16,
              }}
            >
              <KPI
                label="Total Creados"
                value={total}
                accent='var(--brand)'
              />
              <KPI
                label="Pendientes"
                value={pendientes}
                accent='var(--accent)'
              />
              <KPI
                label="Completados"
                value={resueltos}
                accent='var(--gm-success)'
                hint={`${tasaResolucion}% de resolución`}
              />
              <KPI
                label="Sin asignar"
                value={sinAsignar}
                accent='var(--text-muted)'
              />
              <KPI
                label="Vencidos"
                value={vencidos}
                accent='var(--gm-danger)'
              />
              <KPI
                label="T. 1ª respuesta"
                value={fmtDuration(avgFirstResponse)}
                accent='var(--gm-info)'
                hint="promedio"
              />
              <KPI
                label="T. resolución"
                value={fmtDuration(avgResolution)}
                accent='var(--brand)'
                hint="promedio"
              />
              <KPI
                label="Eliminados"
                value={deletedCount}
                accent='var(--text-muted)'
                hint="histórico"
              />
            </div>
          </div>

          {/* Layout de dos columnas: contenido principal + lateral con IA */}
          <div
            style={{
              display: 'flex',
              gap: 16,
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            {/* Columna principal */}
            <div style={{ flex: '3 1 520px', minWidth: 0 }}>
              <div
                style={{ display: activeTab === 'tickets' ? 'block' : 'none' }}
              >
                {/* Tendencia */}
                <GroupLabel>Tendencia</GroupLabel>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginBottom: 16,
                  }}
                >
                  <ChartCard
                    title="Flujo de Tickets (Creados vs Completados)"
                    flex={2}
                  >
                    <div style={{ height: 200 }}>
                      <canvas ref={lineRef} />
                    </div>
                  </ChartCard>
                  <ChartCard title="Estados Actuales" flex={1}>
                    <div style={{ height: 200 }}>
                      <canvas ref={doughnutRef} />
                    </div>
                  </ChartCard>
                </div>

                {/* Distribución */}
                <GroupLabel>Distribución</GroupLabel>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginBottom: 16,
                  }}
                >
                  <ChartCard title="Por Prioridad" flex={1}>
                    <div style={{ height: 200 }}>
                      <canvas ref={priorityRef} />
                    </div>
                  </ChartCard>
                  <ChartCard title="Por Categoría" flex={1}>
                    <div style={{ height: 200 }}>
                      <canvas ref={categoryRef} />
                    </div>
                  </ChartCard>
                  <ChartCard title="Por Origen" flex={1}>
                    <div style={{ height: 200 }}>
                      <canvas ref={barRef} />
                    </div>
                  </ChartCard>
                </div>

                {/* Colas de atención */}
                <GroupLabel accent={C.teal}>
                  Colas de atención
                </GroupLabel>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginBottom: 16,
                  }}
                >
                  <ChartCard title="Tickets por Cola" flex={2}>
                    {queueSummary.length === 0 ? (
                      <EmptyHint>
                        No hay tickets en este rango.
                      </EmptyHint>
                    ) : (
                      <div
                        style={{
                          height: Math.max(140, queueSummary.length * 44 + 60),
                        }}
                      >
                        <canvas ref={queueRef} />
                      </div>
                    )}
                  </ChartCard>
                  <ChartCard
                    title="Desempeño por Cola"
                    flex={2}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            color: 'var(--text-muted)',
                            fontSize: 12,
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          <th style={{ textAlign: 'left', paddingBottom: 8 }}>
                            Cola
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Tickets
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Abiertos
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Vencidos
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Sin asignar
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Resolución
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {queueSummary.map((q) => (
                          <tr
                            key={q.id ?? 'none'}
                            style={{
                              borderBottom: '1px solid var(--border-soft)',
                            }}
                          >
                            <td
                              style={{
                                padding: '9px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: q.color,
                                }}
                              />
                              {q.name}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {q.total}
                            </td>
                            <td style={{ textAlign: 'right' }}>{q.open}</td>
                            <td
                              style={{
                                textAlign: 'right',
                                color: q.overdue ? C.red : undefined,
                                fontWeight: q.overdue ? 600 : undefined,
                              }}
                            >
                              {q.overdue}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {q.unassigned}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {fmtDuration(q.avgResolutionMs)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ChartCard>
                </div>

                {/* Personas */}
                <GroupLabel>Personas</GroupLabel>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginBottom: 16,
                  }}
                >
                  <ChartCard
                    title="Carga por Responsable"
                    flex={1}
                  >
                    <div style={{ height: 240 }}>
                      <canvas ref={responsableRef} />
                    </div>
                  </ChartCard>
                  <ChartCard
                    title="Top Creadores de Tickets"
                    flex={1}
                  >
                    <div style={{ height: 240 }}>
                      <canvas ref={creatorsRef} />
                    </div>
                  </ChartCard>
                </div>

                {/* Detalle */}
                <GroupLabel>Detalle</GroupLabel>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <ChartCard
                    title="Desglose por Estado"
                    flex={1}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            color: 'var(--text-muted)',
                            fontSize: 12,
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          <th style={{ textAlign: 'left', paddingBottom: 8 }}>
                            Estado
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {statusLabels.map((s, i) => (
                          <tr
                            key={s}
                            style={{
                              borderBottom: '1px solid var(--border-soft)',
                            }}
                          >
                            <td
                              style={{
                                padding: '9px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: PALETTE[i % PALETTE.length],
                                }}
                              />
                              {s}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {statusData[i]}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ChartCard>

                  <ChartCard
                    title="Tiempos medios por Prioridad"
                    flex={1}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            color: 'var(--text-muted)',
                            fontSize: 12,
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          <th style={{ textAlign: 'left', paddingBottom: 8 }}>
                            Prioridad
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Tickets
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            1ª resp.
                          </th>
                          <th style={{ textAlign: 'right', paddingBottom: 8 }}>
                            Resolución
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {slaByPriority.map((row) => (
                          <tr
                            key={row.name}
                            style={{
                              borderBottom: '1px solid var(--border-soft)',
                            }}
                          >
                            <td
                              style={{
                                padding: '9px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background:
                                    PRIORITY_COLOR[row.name] || C.slate,
                                }}
                              />
                              {row.name}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {row.total}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {fmtDuration(row.firstResponse)}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {fmtDuration(row.resolution)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ChartCard>
                </div>
              </div>
              <div
                style={{
                  display: activeTab === 'incidents' ? 'block' : 'none',
                }}
              >
                {/* Seguridad (SGSI) — sólo con permiso y con incidentes cargados */}
                {showIncidents && (
                  <>
                    <GroupLabel accent={C.red}>
                      Seguridad · Incidentes SGSI
                    </GroupLabel>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: 12,
                        marginBottom: 12,
                      }}
                    >
                      <KPI
                        label="Incidentes"
                        value={incTotal}
                        accent={C.indigo}
                      />
                      <KPI
                        label="Sin cerrar"
                        value={incAbiertos}
                        accent={C.amber}
                      />
                      <KPI
                        label="Críticos abiertos"
                        value={incCriticos}
                        accent={C.red}
                      />
                      <KPI
                        label="Fuera de plazo"
                        value={incVencidos}
                        accent={C.pink}
                        hint="respuesta vencida"
                      />
                      <KPI
                        label="Datos personales"
                        value={incDatosPersonales}
                        accent={C.teal}
                        hint={`${incProdhab} sin notificar a PRODHAB`}
                      />
                      <KPI
                        label="T. cierre"
                        value={fmtDuration(incAvgClose)}
                        accent={C.green}
                        hint="promedio"
                      />
                    </div>

                    {incTotal === 0 ? (
                      <ChartCard title="Incidentes">
                        <EmptyHint>
                          No hay incidentes detectados en este rango.
                        </EmptyHint>
                      </ChartCard>
                    ) : (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'wrap',
                            marginBottom: 12,
                          }}
                        >
                          <ChartCard
                            title="Detectados vs Cerrados"
                            flex={2}
                          >
                            <div style={{ height: 200 }}>
                              <canvas ref={incTrendRef} />
                            </div>
                          </ChartCard>
                          <ChartCard
                            title="Por Severidad"
                            flex={1}
                          >
                            <div style={{ height: 200 }}>
                              <canvas ref={incSeverityRef} />
                            </div>
                          </ChartCard>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'wrap',
                            marginBottom: 16,
                          }}
                        >
                          <ChartCard
                            title="Por Estado"
                            flex={1}
                          >
                            <div style={{ height: 200 }}>
                              <canvas ref={incStatusRef} />
                            </div>
                          </ChartCard>
                          <ChartCard
                            title="Origen de Detección"
                            flex={2}
                          >
                            <div style={{ height: 200 }}>
                              <canvas ref={incSourceRef} />
                            </div>
                          </ChartCard>
                        </div>
                      </>
                    )}
                  </>
                )}

                {canSeeIncidents && !showIncidents && (
                  <ChartCard title="Incidentes">
                    <EmptyHint>
                      Aún no hay incidentes de seguridad registrados.
                    </EmptyHint>
                  </ChartCard>
                )}
              </div>
            </div>

            {/* Columna lateral: Análisis con IA (sticky) */}
            <div
              style={{
                flex: '1 1 300px',
                minWidth: 280,
                maxWidth: 420,
                position: 'sticky',
                top: 12,
              }}
            >
              <AIAnalysisPanel supabase={supabase} metrics={aiMetrics} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Inicio: soporte ve el dashboard operativo; el resto, un dashboard personal
 * con sus propios tickets. Así quien no es de soporte no consulta ni ve datos
 * de otras personas.
 */
const Home = () => {
  const isSupport = useHasAccess(INCIDENTS_ACCESS)
  return isSupport ? <OperationalDashboard /> : <MyDashboard />
}

export default Home
